import React, { useState, useEffect, useMemo } from "react";
import styles from "./styles.module.css";
import { useUser } from "../../context/UserContext.js";
import api from "../../../api";
import SuccessToast from "components/Toaster/SuccesToast";
import ErrorToast from "components/Toaster/ErrorToast";
import NavbarLoggedInNew from "components/Navbar-new/NavbarLoggedInNew";


function Settings() {
  const { data } = useUser();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    current_profession: "",
    target_profession: "",
    email: "",
    phone_number: "",
    college_name: "",
    years_of_experience: "",
  });

  const [savedData, setSavedData] = useState({});

  useEffect(() => {
    if (data) {
      const initialData = {
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        current_profession: data.current_profession || "",
        target_profession: data.target_profession || "",
        email: data.email || "",
        phone_number: data.phone_number || "",
        college_name: data.college_name || "",
        years_of_experience: data.years_of_experience || "",
      };
      setFormData(initialData);
      setSavedData(initialData);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  
  const isDirty = useMemo(() => {
    return Object.keys(formData).some((key) => formData[key] !== savedData[key]);
  }, [formData, savedData]);

  const changedCount = useMemo(() => {
    return Object.keys(formData).filter((key) => formData[key] !== savedData[key]).length;
  }, [formData, savedData]);

  const profileStrength = useMemo(() => {
    const fields = [
      formData.phone_number,
      formData.current_profession,
      formData.target_profession,
      formData.college_name,
      formData.years_of_experience,
    ];
    const filled = fields.filter((x) => String(x).trim()).length;
    return Math.round(((filled + 3) / (fields.length + 3)) * 100);
  }, [formData]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isDirty) return;

    setSaving(true);
    setShowSuccessToast(false);
    setShowErrorToast(false);

    try {
      const response = await api.post("user-profile/", formData);
      if (response.status === 201) {
        setSavedData({ ...formData });
        setShowSuccessToast(true);
        setError(null);
        setTimeout(() => setShowSuccessToast(false), 3000);
      } else {
        setError("Failed to update profile.");
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 3000);
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("An error occurred while updating your profile.");
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (isDirty) {
      setFormData({ ...savedData });
    }
  };

  const initial = formData.first_name ? formData.first_name.charAt(0).toUpperCase() : "?";
  const fullName = `${formData.first_name || ""} ${formData.last_name || ""}`.trim() || "User";

  return (
    <>
      <NavbarLoggedInNew hideMenu={false} />
      <div className={styles.pageContainer}>
        {showSuccessToast && <SuccessToast message="Profile updated successfully!" />}
        {showErrorToast && <ErrorToast message={error} />}

        <div className={styles.content}>
          {/* Profile Header Card */}
          <div className={styles.profileCard}>
            <div className={styles.avatar}>{initial}</div>
            <div className={styles.profileInfo}>
              <h1 className={styles.fullName}>{fullName}</h1>
              <div className={styles.professionTags}>
                {formData.current_profession && (
                  <span className={styles.currentTag}>{formData.current_profession}</span>
                )}
                {formData.current_profession && formData.target_profession && (
                  <span className={styles.arrow}>→</span>
                )}
                {formData.target_profession && (
                  <span className={styles.targetTag}>{formData.target_profession}</span>
                )}
              </div>
              <div className={styles.contactInfo}>
                {formData.email && <span>{formData.email}</span>}
                {formData.phone_number && <span>+91 {formData.phone_number}</span>}
                {formData.college_name && <span>{formData.college_name}</span>}
              </div>
            </div>
            <div className={styles.strengthBox}>
              <div className={styles.strengthHeader}>
                <span className={styles.strengthLabel}>Profile strength</span>
                <span
                  className={styles.strengthPercent}
                  style={{ color: profileStrength === 100 ? "#1f9d4d" : "#0172A6" }}
                >
                  {profileStrength}%
                </span>
              </div>
              <div className={styles.strengthBar}>
                <div
                  className={styles.strengthFill}
                  style={{
                    width: `${profileStrength}%`,
                    background: profileStrength === 100 ? "#1f9d4d" : "#0172A6",
                  }}
                />
              </div>
              <span className={styles.strengthHint}>
                {profileStrength === 100
                  ? "Complete — nice."
                  : !formData.years_of_experience
                  ? "Add years of experience to reach 100%"
                  : "Fill the remaining fields to reach 100%"}
              </span>
            </div>
          </div>

          {/* Form Card */}
          <div className={styles.formCard}>
            {/* Personal Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionLeft}>
                <h2 className={styles.sectionTitle}>Personal</h2>
                <p className={styles.sectionDesc}>
                  Your name comes from your enrolment record. Contact support to change it.
                </p>
              </div>
              <div className={styles.sectionRight}>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>First name</label>
                    <div className={styles.lockedInput}>
                      <span>{formData.first_name}</span>
                      <span className={styles.lockIcon}>🔒</span>
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>Last name</label>
                    <div className={styles.lockedInput}>
                      <span>{formData.last_name}</span>
                      <span className={styles.lockIcon}>🔒</span>
                    </div>
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Email</label>
                    <div className={styles.lockedInput}>
                      <span className={styles.emailText}>{formData.email}</span>
                      <span className={styles.lockIcon}>🔒</span>
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>Phone number</label>
                    <div className={styles.phoneInput}>
                      <span className={styles.phonePrefix}>+91</span>
                      <input
                        type="text"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.sectionDivider} />

            {/* Career Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionLeft}>
                <h2 className={styles.sectionTitle}>Career</h2>
                <p className={styles.sectionDesc}>
                  Your mentor uses this to tailor your learning path and mock interviews.
                </p>
              </div>
              <div className={styles.sectionRight}>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label>Current profession</label>
                    <input
                      type="text"
                      name="current_profession"
                      value={formData.current_profession}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Target profession</label>
                    <input
                      type="text"
                      name="target_profession"
                      value={formData.target_profession}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Years of experience</label>
                  <div className={styles.expInputRow}>
                    <input
                      type="number"
                      name="years_of_experience"
                      value={formData.years_of_experience}
                      onChange={handleInputChange}
                      min="0"
                      max="50"
                      placeholder="e.g. 5"
                    />
                    <span className={styles.expSuffix}>years</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.sectionDivider} />

            {/* Education Section */}
            <div className={styles.formSection}>
              <div className={styles.sectionLeft}>
                <h2 className={styles.sectionTitle}>Education</h2>
                <p className={styles.sectionDesc}>Where you studied most recently.</p>
              </div>
              <div className={styles.sectionRight}>
                <div className={styles.field}>
                  <label>College</label>
                  <input
                    type="text"
                    name="college_name"
                    value={formData.college_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className={styles.footer}>
          <div className={styles.footerContent}>
            <div
              className={styles.statusDot}
              style={{ background: isDirty ? "#e0a030" : "#1f9d4d" }}
            />
            <span className={styles.statusText}>
              {isDirty
                ? `${changedCount} unsaved ${changedCount === 1 ? "change" : "changes"}`
                : showSuccessToast
                ? "Saved just now"
                : "All changes saved"}
            </span>
            <button
              type="button"
              className={styles.discardBtn}
              onClick={handleDiscard}
              disabled={!isDirty}
            >
              Discard
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSubmit}
              disabled={!isDirty || saving}
              style={{ background: isDirty ? "#0172A6" : "#a9c9db" }}
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Settings;
